// frontend/app/i18n/glossary.js
// AIT-251: Single source of truth for the three concepts: Quiz, Test, Homework Help.
//
// Why this file exists:
//   The UI historically used "Homework", "Quiz", and "Test" interchangeably.
//   This module pins each concept to one icon, one accent color family, one label
//   translation key, and one definition translation key — so every surface that
//   displays one of these kinds renders identically.
//
// Definitions (matched to actual code behavior — see docs/tickets/AIT-251.md):
//   - Quiz:     short ungraded per-topic check (no dedicated backend table)
//   - Test:     teacher-created assessment (assigned) OR student-built (practice)
//               — both live in `tests` table with `test_kind = "assigned" | "practice"`
//   - Homework: Homework Help — student uploads a worksheet, AI walks them through
//               problems. NOT teacher-assigned graded work.

// Frozen so callers cannot mutate the shared singletons.
export const GLOSSARY = Object.freeze({
  quiz: Object.freeze({
    labelKey: 'Quiz',
    definitionKey: 'Glossary.Quiz.Definition',
    iconName: 'lightbulb-outline',
    accent: 'blue',
  }),
  test: Object.freeze({
    labelKey: 'Test',
    definitionKey: 'Glossary.Test.Definition',
    iconName: 'clipboard-text-outline',
    accent: 'purple',
  }),
  homework: Object.freeze({
    labelKey: 'Homework Help',
    definitionKey: 'Glossary.Homework.Definition',
    iconName: 'book-open-variant',
    accent: 'amber',
  }),
});

export const GLOSSARY_KINDS = Object.freeze(['quiz', 'test', 'homework']);

// Accent palette — paired with dark mode variants per MEMORY.md convention.
// Keep in sync with `glossaryAccent*` tokens in MainContentArea.getMainContentColors.
const ACCENT_COLORS = Object.freeze({
  blue: { light: '#3B82F6', dark: '#60a5fa' },
  purple: { light: '#8B5CF6', dark: '#a78bfa' },
  amber: { light: '#f59e0b', dark: '#fbbf24' },
});

const normalizeKind = (kind) => {
  if (typeof kind !== 'string' || kind.length === 0) return null;
  return kind.toLowerCase();
};

/**
 * Returns the glossary entry for a given kind, or null if unknown.
 * @param {string} kind - "quiz" | "test" | "homework" (case-insensitive)
 * @returns {{labelKey: string, definitionKey: string, iconName: string, accent: string} | null}
 */
export function getGlossaryEntry(kind) {
  const k = normalizeKind(kind);
  if (!k) return null;
  return GLOSSARY[k] || null;
}

/**
 * Resolves a kind into props ready for <GlossaryTooltip />.
 * Returns null if the kind is unknown.
 * @param {string} kind
 * @param {(key: string) => string} [t] - translation function; defaults to identity.
 */
export function getGlossaryTooltipProps(kind, t) {
  const entry = getGlossaryEntry(kind);
  if (!entry) return null;
  const translate = typeof t === 'function' ? t : (key) => key;
  return {
    kind: normalizeKind(kind),
    label: translate(entry.labelKey),
    definition: translate(entry.definitionKey),
    iconName: entry.iconName,
    accent: entry.accent,
  };
}

/**
 * Returns the hex color string for a given accent family in light or dark mode.
 * @param {string} accent - "blue" | "purple" | "amber"
 * @param {boolean} isDarkMode
 * @returns {string | null}
 */
export function getGlossaryAccentColor(accent, isDarkMode) {
  if (typeof accent !== 'string' || !accent) return null;
  const swatch = ACCENT_COLORS[accent];
  if (!swatch) return null;
  return isDarkMode ? swatch.dark : swatch.light;
}
